import { RateMarketplaceBannerImg } from 'components/_common/Icons';
import BellSVG from 'components/_common/Icons/SVGIcons/BellSVG';
import BoxSvg from 'components/_common/Icons/SVGIcons/BoxSvg';
import ChartLineSvg from 'components/_common/Icons/SVGIcons/ChartLineSvg';
import ChevronRightSvg from 'components/_common/Icons/SVGIcons/ChevronRightSvg';
import CompareSvg from 'components/_common/Icons/SVGIcons/CompareSvg';
import DiscountCouponOfferSvg from 'components/_common/Icons/SVGIcons/DiscountCouponOfferSvg';
import FAQIconSvg from 'components/_common/Icons/SVGIcons/FAQIconSvg';
import FFLLoginSvg from 'components/_common/Icons/SVGIcons/FFLLoginSvg';
import GunIconSvg from 'components/_common/Icons/SVGIcons/GunIconSvg';
import HeartIconSvg from 'components/_common/Icons/SVGIcons/HeartIconSvg';
import LeftChevronSvg from 'components/_common/Icons/SVGIcons/LeftChevronSvg';
import ReviewIconSvg from 'components/_common/Icons/SVGIcons/ReviewIconSvg';
import UserCircleSvg from 'components/_common/Icons/SVGIcons/UserCircleSvg';
import UsersIconSvg from 'components/_common/Icons/SVGIcons/UsersIconSvg';
import VerifiedBadgeSvg from 'components/_common/Icons/SVGIcons/VerifiedBadgeSvg';
import {
  getFullName,
  getStringPrefix,
  getTruncatedString,
  placeParamsToURL,
} from 'helpers/CommonHelper';
import {
  displayMobileNumberWithCode,
  isRegisteredFfl,
  MARKETPLACE_SLUG,
  SHIPPING_FFL_NAME_MAX_LENGTH,
} from 'helpers/DataHelper';
import { getImageURL } from 'helpers/FileUploadHelper';
import { notify } from 'helpers/NotifyHelper';
import { confirmAlert } from 'helpers/SwalAlertHelper';
import { t } from 'i18next';
import AddReviewModal from 'modules/customer/components/_common/ModuleSections/CustomerReviews/Modals/AddReviewModal';
import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { loginRoutes } from 'routes/admin/AdminRoutes';
import {
  customerMainRoutes,
  customerProfileRoutes,
  getTradeInPath,
  TRADE_IN_BASE_PATH,
} from 'routes/customer/CustomerRoutes';
import { logoutUser } from 'store/auth/actions/AuthAction';
import { userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import { compareTotalSelector } from 'store/customer/selectors/customerCompareSelectors';
import {
  selectedDeliveryAddressSelector,
  selectedDeliveryFFLNameSelector,
  totalFollowingFFLSelector,
  totalGunForSaleSelector,
  totalUnreadNotificationSelector,
} from 'store/customer/selectors/CustomerGlobalSelectors';
import { wishListTotalProductSelector } from 'store/customer/selectors/customerWishListSelectors';
import {
  setDeliveryLocationModalOpen,
  setGuestDeliveryFFLModalOpen,
} from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

// menu items pointing to a tabbed page stay highlighted on every tab of that page
const isBasePathActive = (pathname: string, basePath: string) =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

interface ProfileDropdownProps {
  onClose?: () => void;
  isSidebar?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isMobileDrawer?: boolean;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  onClose,
  isSidebar = false,
  isCollapsed = false,
  onToggleCollapse,
  isMobileDrawer = false,
}) => {
  const userProfileInfo = useAppSelector(userProfileInfoSelector);
  const totalFollowingFFL = useAppSelector(totalFollowingFFLSelector);
  const totalWishListProduct = useAppSelector(wishListTotalProductSelector);
  const totalUnreadNotification = useAppSelector(totalUnreadNotificationSelector);
  const totalGunForSale = useAppSelector(totalGunForSaleSelector);
  const totalCompareProducts = useAppSelector(compareTotalSelector);
  // follows the address picked in the header "Deliver to" block
  const selectedFFLAddress = useAppSelector(selectedDeliveryAddressSelector);
  const selectedFFLName = useAppSelector(selectedDeliveryFFLNameSelector);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // "Rate the marketplace" promo at the bottom of the panel
  const [rateModalVisible, setRateModalVisible] = useState(false);

  // opens the same "Select Delivery Location" popup as the header "Deliver to" block
  // (the FFL map picker for guests, who have no address book)
  const handleOpenDeliveryLocation = useCallback(() => {
    dispatch(
      userProfileInfo?.id ? setDeliveryLocationModalOpen(true) : setGuestDeliveryFFLModalOpen(true)
    );
    onClose?.();
  }, [dispatch, onClose, userProfileInfo?.id]);

  useEffect(() => {
    if (!onClose) return;
    const handleClickOutside = (e: MouseEvent) => {
      // The review modal is portalled to <body>, so every click inside it reads as "outside" the
      // dropdown - closing here would unmount the modal along with the panel.
      if (rateModalVisible) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, rateModalVisible]);

  const verifyAuthenticatedUser = useCallback(
    (e: React.MouseEvent) => {
      if (!userProfileInfo) {
        e.preventDefault();
        navigate(loginRoutes.login.path);
      }
    },
    [navigate, userProfileInfo]
  );

  const confirmLogout = useCallback(async () => {
    confirmAlert(
      t('confirm_title'),
      t('confirm_logout_msg'),
      async () => {
        const response = await logoutUser();
        if (!response && response.status) {
          notify.error(response.message);
        } else {
          if (onClose) onClose();
          navigate(customerMainRoutes.customerHome.path);
        }
      },
      { confirmBtnText: t('yes_confirm_logout_btn_text') }
    );
  }, [navigate, onClose]);

  // Promo strip pinned under the logout row. It opens the same marketplace review popup the
  // reviews page uses, so a rating can be left without leaving the current page.
  const rateMarketplaceBanner = (
    <div className="profile-rate-banner">
      <img
        src={RateMarketplaceBannerImg}
        alt=""
        aria-hidden="true"
        className="profile-rate-banner__bg"
      />
      <div className="profile-rate-banner__overlay" />
      <div className="profile-rate-banner__content">
        <p className="profile-rate-banner__title">
          {t('loving_mylocalgunshop', 'LOVING MYLOCALGUNSHOP?')}
        </p>
        <button
          type="button"
          className="profile-rate-banner__btn"
          onClick={() => setRateModalVisible(true)}
        >
          {t('rate_the_marketplace', 'Rate the marketplace')}
        </button>
      </div>
    </div>
  );

  const rateMarketplaceModal = rateModalVisible ? (
    <AddReviewModal
      visible={rateModalVisible}
      module={MARKETPLACE_SLUG}
      details={{ id: 'market', name: 'Marketplace' }}
      activeTab="product"
      onCancel={() => setRateModalVisible(false)}
      onSubmit={() => setRateModalVisible(false)}
    />
  ) : null;

  const menuItems = useMemo(
    () => [
      {
        title: t('overview'),
        subtitle: 'Overview of the current status',
        icon: <ChartLineSvg width={20} height={20} />,
        to: customerProfileRoutes.profileOverview.path,
      },
      {
        title: t('orders'),
        subtitle: t('track_shipments_and_ffl_transfers'),
        icon: <BoxSvg width={18} height={18} stroke="currentColor" fill="currentColor" />,
        to: customerProfileRoutes.orders.path,
      },
      {
        title: t('offers'),
        subtitle: t('active_listing_and_bids'),
        icon: <DiscountCouponOfferSvg width={18} height={18} />,
        to: customerProfileRoutes.offersList.path,
      },
      {
        title: t('trade_in'),
        subtitle: t('trade_in_items'),
        icon: <GunIconSvg width={18} height={18} />,
        to: getTradeInPath(),
        activeBasePath: TRADE_IN_BASE_PATH,
        badge: totalGunForSale,
      },
      {
        title: t('favorites'),
        subtitle: t('saved_items'),
        icon: <HeartIconSvg width={18} height={18} />,
        to: placeParamsToURL(customerProfileRoutes.favoriteList.path),
        onClick: verifyAuthenticatedUser,
        badge: totalWishListProduct,
      },
      {
        title: t('following'),
        subtitle: t('follow_your_favorite_ffl'),
        icon: <UsersIconSvg width={18} height={18} />,
        to: customerProfileRoutes.followingList.path,
        onClick: verifyAuthenticatedUser,
        badge: totalFollowingFFL,
      },
      {
        title: t('compare_items'),
        subtitle: t('compare_multiple_products'),
        icon: <CompareSvg width={18} height={18} />,
        to: customerProfileRoutes.compareList.path,
        badge: totalCompareProducts,
      },
      {
        title: t('notifications'),
        subtitle: t('notification_from_mlgs'),
        icon: <BellSVG width={18} height={18} stroke={'currentColor'} />,
        badge: totalUnreadNotification,
        to: customerProfileRoutes.notificationList.path,
      },
      {
        title: t('reviews'),
        subtitle: t('manage_profile_reviews'),
        icon: <ReviewIconSvg width={18} height={18} />,
        to: customerProfileRoutes.reviews.path,
      },
      {
        title: t('profile_and_settings'),
        subtitle: t('manage_profile_and_addresses'),
        icon: <UserCircleSvg width={20} height={20} />,
        to: customerProfileRoutes.profileGeneralInfo.path,
      },
      {
        title: t('help_and_support'),
        subtitle: t('help_and_support_subtitle'),
        icon: <FAQIconSvg width={18} height={18} />,
        to: customerProfileRoutes.helpAndSupport.path,
      },
    ],
    [
      totalUnreadNotification,
      totalGunForSale,
      verifyAuthenticatedUser,
      totalWishListProduct,
      totalFollowingFFL,
      totalCompareProducts,
    ]
  );

  const mobileMenuItems = useMemo(
    () => [
      {
        title: 'OVERVIEW',
        subtitle: 'Overview of the current status',
        icon: <ChartLineSvg width={18} height={18} />,
        to: customerProfileRoutes.profileOverview.path,
      },
      {
        title: 'ORDERS',
        subtitle: t('track_shipments_and_ffl_transfers', 'Track shipments & FFL transfers'),
        icon: <BoxSvg width={18} height={18} stroke="currentColor" fill="currentColor" />,
        to: customerProfileRoutes.orders.path,
      },
      {
        title: 'OFFERS',
        subtitle: t('active_listing_and_bids', 'Active listings & bids'),
        icon: <DiscountCouponOfferSvg width={18} height={18} />,
        to: customerProfileRoutes.offersList.path,
        badge: totalGunForSale || 12,
      },
      {
        title: 'TRADE-IN',
        subtitle: t('trade_in_items', 'Trade-In Items'),
        icon: <GunIconSvg width={18} height={18} />,
        to: getTradeInPath(),
        activeBasePath: TRADE_IN_BASE_PATH,
        badge: totalGunForSale || 12,
      },
      {
        title: 'Favorite',
        subtitle: t('saved_items', 'Saved items'),
        icon: <HeartIconSvg width={18} height={18} />,
        to: placeParamsToURL(customerProfileRoutes.favoriteList.path),
        onClick: verifyAuthenticatedUser,
        badge: totalWishListProduct,
      },
      {
        title: 'FOLLOWING',
        subtitle: t('follow_your_favorite_ffl', 'Follow your favorite FFL'),
        icon: <UsersIconSvg width={18} height={18} />,
        to: customerProfileRoutes.followingList.path,
        onClick: verifyAuthenticatedUser,
        badge: totalFollowingFFL || 2,
      },
      {
        title: 'COMPARE ITEMS',
        subtitle: t('compare_multiple_products', 'Compare multiple products'),
        icon: <CompareSvg width={18} height={18} />,
        to: customerProfileRoutes.compareList.path,
        badge: totalCompareProducts,
      },
      {
        title: 'NOTIFICATIONS',
        subtitle: t('notification_from_mlgs', 'Notification from MLGS'),
        icon: <BellSVG className="bell-icon" width={18} height={18} />,
        badge: totalUnreadNotification || 10,
        to: customerProfileRoutes.notificationList.path,
      },
      {
        title: 'REVIEWS',
        subtitle: t('manage_profile_reviews', 'Manage Profile Reviews'),
        icon: <ReviewIconSvg width={18} height={18} />,
        to: customerProfileRoutes.reviews.path,
      },
      {
        title: 'PROFILE & SETTINGS',
        subtitle: t('manage_profile_and_addresses', 'Manage Profile & Addresses'),
        icon: <UserCircleSvg width={18} height={18} />,
        to: customerProfileRoutes.profileGeneralInfo.path,
      },
      {
        title: 'HELP & SUPPORT',
        subtitle: t('follow_your_favorite_ffl', 'Follow your favorite FFL'),
        icon: <FAQIconSvg width={18} height={18} />,
        to: customerProfileRoutes.helpAndSupport.path,
      },
    ],
    [
      totalUnreadNotification,
      totalGunForSale,
      verifyAuthenticatedUser,
      totalWishListProduct,
      totalFollowingFFL,
      totalCompareProducts,
    ]
  );

  if (isMobileDrawer) {
    if (userProfileInfo) {
      return (
        <div className="mobile-profile-drawer">
          <div className="mobile-profile-drawer__user-section">
            <div className="mobile-profile-drawer__user-header">
              <div className="mobile-profile-drawer__avatar">
                {userProfileInfo.profile_pic ? (
                  <img
                    src={getImageURL(userProfileInfo.profile_pic)}
                    alt="User Profile"
                    className="avatar-img"
                  />
                ) : (
                  <div className="avatar-initials">
                    {getStringPrefix(`${userProfileInfo.first_name} ${userProfileInfo.last_name}`)}
                  </div>
                )}
              </div>
              <div className="mobile-profile-drawer__user-details flex-grow-1 min-w-0">
                <h5
                  className="user-name text-truncate"
                  title={getFullName(userProfileInfo.first_name, userProfileInfo.last_name)}
                >
                  {getFullName(userProfileInfo.first_name, userProfileInfo.last_name).toUpperCase()}
                </h5>
                <div className="user-contact">
                  <p className="user-email text-truncate" title={userProfileInfo.email}>
                    {userProfileInfo.email}
                  </p>
                  {userProfileInfo.mobile && (
                    <p className="user-phone">
                      {displayMobileNumberWithCode(
                        userProfileInfo.mobile,
                        userProfileInfo.mobile_code
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mobile-profile-drawer__ffl-banner">
              <span className="ffl-text">
                {t('your_selected_ffl_for_shipping', 'Your selected FFL for Shipping')} -{' '}
                <span
                  className="ffl-link cursor-pointer"
                  title={selectedFFLName || t('choose_ffl')}
                  onClick={handleOpenDeliveryLocation}
                >
                  {selectedFFLName
                    ? getTruncatedString(selectedFFLName, SHIPPING_FFL_NAME_MAX_LENGTH)
                    : t('choose_ffl')}
                </span>
              </span>
              {selectedFFLName && isRegisteredFfl(selectedFFLAddress?.selected_ffl) && (
                <VerifiedBadgeSvg width={14} height={14} className="verified-icon" />
              )}
            </div>
          </div>

          <nav className="mobile-profile-drawer__menu">
            {mobileMenuItems.map((item: any, index: number) => {
              const hasBadge = item.badge !== undefined && item.badge !== null && item.badge > 0;
              const content = (
                <>
                  <div className="mobile-profile-drawer__icon-circle">{item.icon}</div>
                  <div className="mobile-profile-drawer__item-info">
                    <span className="item-title">{item.title}</span>
                    <span className="item-subtitle">{item.subtitle}</span>
                  </div>
                  <div className="mobile-profile-drawer__item-end">
                    {hasBadge && <span className="item-badge">{item.badge}</span>}
                    <ChevronRightSvg width={10} height={10} />
                  </div>
                </>
              );

              if (item.to) {
                return (
                  <NavLink
                    key={index}
                    to={item.to}
                    className={({ isActive }) => {
                      const active = item.activeBasePath
                        ? isBasePathActive(location.pathname, item.activeBasePath)
                        : isActive;
                      return `mobile-profile-drawer__menu-item ${active && item.to !== '#' ? 'active' : ''}`;
                    }}
                    onClick={(e) => {
                      if (item.onClick) item.onClick(e);
                      if (onClose) onClose();
                    }}
                  >
                    {content}
                  </NavLink>
                );
              }

              return (
                <button
                  key={index}
                  type="button"
                  className="mobile-profile-drawer__menu-item"
                  onClick={(e) => {
                    if (item.onClick) item.onClick(e);
                    if (onClose) onClose();
                  }}
                >
                  {content}
                </button>
              );
            })}

            <button
              type="button"
              className="mobile-profile-drawer__menu-item logout-item"
              onClick={() => {
                if (onClose) onClose();
                confirmLogout();
              }}
            >
              <div className="mobile-profile-drawer__logout-icon">
                <FFLLoginSvg width={20} height={20} />
              </div>
              <div className="mobile-profile-drawer__item-info">
                <span className="item-title logout-title">{t('logout', 'LOGOUT')}</span>
              </div>
            </button>
          </nav>

          {rateMarketplaceBanner}
          {rateMarketplaceModal}
        </div>
      );
    } else {
      return (
        <div className="mobile-profile-drawer">
          <div className="mobile-profile-drawer__user-section p-4 text-center">
            <h5 className="user-name mb-2">WELCOME GUEST</h5>
            <p className="user-subtitle">Manage cart, orders & favorites</p>
          </div>
          <div className="d-flex flex-column gap-2 p-3">
            <Link
              to={placeParamsToURL(loginRoutes.customerSignup.path)}
              className="btn btn-orange-900"
              onClick={onClose}
            >
              {t('create_account')}
            </Link>
            <Link
              to={placeParamsToURL(loginRoutes.login.path)}
              className="btn btn-orange"
              onClick={onClose}
            >
              {t('sign_in')}
            </Link>
          </div>
        </div>
      );
    }
  }

  const containerClassName = `profile-dropdown ${isSidebar ? 'profile-sidebar' : ''} ${
    isSidebar && isCollapsed ? 'profile-sidebar--collapsed' : ''
  }`;

  if (userProfileInfo) {
    return (
      <div className={containerClassName} ref={ref}>
        <div className="profile-dropdown__header-wrapper">
          {isSidebar && onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="profile-sidebar-toggle-btn"
              type="button"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <>
                  <ChevronRightSvg width={10} height={10} />
                  {/*  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg> */}
                </>
              ) : (
                <>
                  <LeftChevronSvg width={14} height={13} />
                  {/* <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg> */}
                </>
              )}
            </button>
          )}

          <div className="profile-dropdown__header">
            <div
              className={`profile-dropdown__avatar-square ${
                userProfileInfo.profile_pic ? 'has-image' : ''
              }`}
            >
              {userProfileInfo.profile_pic ? (
                <img
                  src={getImageURL(userProfileInfo.profile_pic)}
                  alt="User Profile"
                  className="profile-avatar-img"
                />
              ) : (
                <div className="profile-avatar-initials">
                  {getStringPrefix(`${userProfileInfo.first_name} ${userProfileInfo.last_name}`)}
                </div>
              )}
            </div>
            <div className="profile-dropdown__info w-100">
              <p
                className="profile-dropdown__name mb-0"
                title={getFullName(userProfileInfo.first_name, userProfileInfo.last_name)}
              >
                {getTruncatedString(
                  getFullName(userProfileInfo.first_name, userProfileInfo.last_name),
                  20
                )}
              </p>
              <div className="profile-dropdown__contact">
                <p className="profile-dropdown__email" title={userProfileInfo.email}>
                  {getTruncatedString(userProfileInfo.email, 30)}
                </p>
                {userProfileInfo.mobile && (
                  <p className="profile-dropdown__phone">
                    {displayMobileNumberWithCode(
                      userProfileInfo.mobile,
                      userProfileInfo.mobile_code
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="profile-dropdown__ffl-banner">
            <span className="profile-dropdown__ffl-text">
              {t('your_selected_ffl_for_shipping')} -
              <span
                className="profile-dropdown__ffl-link ms-1 cursor-pointer"
                title={selectedFFLName || t('choose_ffl')}
                onClick={handleOpenDeliveryLocation}
              >
                {selectedFFLName
                  ? getTruncatedString(selectedFFLName, SHIPPING_FFL_NAME_MAX_LENGTH)
                  : t('choose_ffl')}
              </span>
            </span>
            {selectedFFLName && isRegisteredFfl(selectedFFLAddress?.selected_ffl) && (
              <VerifiedBadgeSvg
                width={14}
                height={14}
                fill="currentColor"
                className="profile-dropdown__ffl-check"
              />
            )}
          </div>
        </div>

        <nav className="profile-dropdown__menu">
          {menuItems.map((item: any, index) => {
            const hasBadge = item?.badge > 0;
            const content = (
              <>
                <div className="profile-dropdown__item-icon-wrapper">{item.icon}</div>
                <div className="profile-dropdown__item-content">
                  <span className="profile-dropdown__item-title">{item.title}</span>
                  <span className="profile-dropdown__item-subtitle">{item.subtitle}</span>
                </div>
                <div className="profile-dropdown__item-right">
                  {hasBadge && <span className="profile-dropdown__item-badge">{item.badge}</span>}
                  <ChevronRightSvg />
                </div>
              </>
            );

            if (item.to) {
              return (
                <NavLink
                  key={index}
                  to={item.to}
                  title={isCollapsed ? item.title : undefined}
                  className={({ isActive }) => {
                    const active = item.activeBasePath
                      ? isBasePathActive(location.pathname, item.activeBasePath)
                      : isActive;
                    return active && item.to !== '#' && isSidebar
                      ? 'profile-dropdown__menu-item active'
                      : 'profile-dropdown__menu-item';
                  }}
                  onClick={(e) => {
                    if (item.onClick) item.onClick(e);
                    if (onClose) onClose();
                  }}
                >
                  {content}
                </NavLink>
              );
            }

            return (
              <button
                key={index}
                type="button"
                title={isCollapsed ? item.title : undefined}
                className="profile-dropdown__menu-item"
                onClick={(e) => {
                  if (item.onClick) item.onClick(e);
                  if (onClose) onClose();
                }}
              >
                {content}
              </button>
            );
          })}
        </nav>

        <div className="profile-dropdown__footer">
          <button
            className="profile-dropdown__logout"
            onClick={confirmLogout}
            title={isCollapsed ? t('logout') : undefined}
          >
            <span className="profile-dropdown__logout-icon">
              <FFLLoginSvg width={20} height={20} />
            </span>
            <span className="profile-dropdown__logout-text">{t('logout')}</span>
          </button>
        </div>
        {isSidebar && (
          <>
            {/* The collapsed sidebar has no room for it, so the banner is hidden there in CSS. */}
            {rateMarketplaceBanner}
            {rateMarketplaceModal}
          </>
        )}
      </div>
    );
  } else {
    return (
      <div className={containerClassName} ref={ref}>
        <div className="profile-dropdown__header-wrapper">
          <div className="profile-dropdown__header">
            <div className="profile-dropdown__info">
              <p className="profile-dropdown__name">WELCOME GUEST</p>
            </div>
          </div>
          <div className="profile-dropdown__subtitle">
            <p>Manage cart, orders & favorites</p>
          </div>
        </div>

        <div className="profile-dropdown__guest-actions d-flex flex-column">
          <Link
            to={placeParamsToURL(loginRoutes.customerSignup.path)}
            className="btn btn-orange-900"
          >
            {t('create_account')}
          </Link>
          <Link to={placeParamsToURL(loginRoutes.login.path)} className="btn btn-orange">
            {t('sign_in')}
          </Link>
          <div className="profile-dropdown__ffl_subtitle">
            <span>{t('are_you_an_ffl')}</span>
          </div>

          <Link to={placeParamsToURL(loginRoutes.signup.path)} className="btn btn-orange-900">
            {t('join_as_an_ffl')}
          </Link>
          <Link to={placeParamsToURL(loginRoutes.login.path)} className="btn btn-orange">
            {t('ffl_sign_in')}
          </Link>
        </div>
      </div>
    );
  }
};

export default ProfileDropdown;
